import { useEffect, useMemo, useState } from 'react'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Textarea } from '@/components/ui/textarea'
import { Switch } from '@/components/ui/switch'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { toast } from 'sonner'
import { useAppDispatch, useAppSelector } from '@/redux/hooks'
import { setUploadedFiles } from '@/redux/slices/configurationSlice'
import LLMConfigSection from './LLMConfigSection'
import RAGConfigSection from './RAGConfigSection'
import FileUpload from './FileUpload'
import AIPlayerConfigSideBar, { AIPlayerSection } from './AIPlayerConfigSideBar'
import { AnyMCPServer } from '@/api/remoteMCP'
import { DummyBot } from '@/api/dummyBot'
import { updateDummyBotThunk } from '@/redux/asyncThunks/dummyBot'
import { LLMChoice } from '@/redux/types/room'
import { Checkbox } from '../ui/Checkbox'
import { Card, CardContent } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { cn } from '@/lib/utils'
import { SidebarProvider } from '@/components/ui/sidebar'
import { AlertCircle, FlaskConical, History, MessageSquare } from 'lucide-react'
import ChatBoxesRenderer from './ChatBoxesRenderer'
import { TestChatType } from '@/constants/chat'
import PreviousAIPlayerTestChatsModal from './PreviousAIPlayerTestChatsModal'
import {
  addAIPlayerOpenChatBox,
  addAIPlayerTestChatBox,
  clearAIPlayerTestChatMessages,
  clearAllAIPlayerOpenChatBoxes,
  removeAIPlayerOpenChatBox,
  removeAIPlayerTestChatBox,
  setIsAIPlayerTestChatOpen,
} from '@/redux/slices/aiPlayerTestChatSlice'
import {
  closeAIPlayerTestChat,
  getPreviousAIPlayerChats,
} from '@/redux/asyncThunks/aiPlayerTestChatWebsocket'

interface Props {
  bot: DummyBot
  onSaved?: () => Promise<void> | void
  servers: AnyMCPServer[]
  ownerConnectedIds?: Set<number>
}

export default function AIPlayerConfigForm({ bot, onSaved, servers, ownerConnectedIds = new Set() }: Props) {
  const dispatch = useAppDispatch()
  const { files: uploadedFiles = [] } = useAppSelector((s) => s.configuration)
  const { openChatBoxes, existingTestChats } = useAppSelector(
    (s) => s.aiPlayerTestChatMessages
  )
  const chatBoxesOpen = openChatBoxes.length > 0
  const [previousChatModalOpen, setPreviousChatModalOpen] = useState<boolean>(false)
  const [activeSection, setActiveSection] = useState<AIPlayerSection>(
    AIPlayerSection.PERSONA
  )
  const [name, setName] = useState<string>(bot.name)
  const [persona, setPersona] = useState<string>(bot.persona_instructions || '')
  const [lockDuringRound, setLockDuringRound] = useState<boolean>(
    !!bot.lock_during_active_round
  )
  const [chatbotLlm, setChatbotLlm] = useState<LLMChoice>(
    bot.chatbot_llm ?? LLMChoice.CLAUDE
  )
  const [llmConfig, setLlmConfig] = useState({
    temperature: bot.temperature ?? 0.7,
    maxTokens: bot.max_tokens ?? 1024,
    historyLimit: bot.history_limit ?? 25,
  })
  const [ragConfig, setRagConfig] = useState({
    ragEnabled: bot.rag_enabled ?? true,
    ragTopK: bot.rag_top_k ?? 5,
    ragChunkSize: bot.rag_chunk_size ?? 1000,
    ragChunkOverlap: bot.rag_chunk_overlap ?? 200,
    ragSimilarityThreshold: bot.rag_similarity_threshold ?? 0.2,
    ragMaxContextLength: bot.rag_max_context_length ?? 3000,
  })
  const [mcpServerIds, setMcpServerIds] = useState<number[]>(
    (bot.mcp_servers || []).map((s) => s.id)
  )
  const [isSaving, setIsSaving] = useState<boolean>(false)

  useEffect(() => {
    setName(bot.name)
    setPersona(bot.persona_instructions || '')
    setLockDuringRound(!!bot.lock_during_active_round)
    setChatbotLlm(bot.chatbot_llm ?? LLMChoice.CLAUDE)
    setLlmConfig({
      temperature: bot.temperature ?? 0.7,
      maxTokens: bot.max_tokens ?? 1024,
      historyLimit: bot.history_limit ?? 25,
    })
    setRagConfig({
      ragEnabled: bot.rag_enabled ?? true,
      ragTopK: bot.rag_top_k ?? 5,
      ragChunkSize: bot.rag_chunk_size ?? 1000,
      ragChunkOverlap: bot.rag_chunk_overlap ?? 200,
      ragSimilarityThreshold: bot.rag_similarity_threshold ?? 0.2,
      ragMaxContextLength: bot.rag_max_context_length ?? 3000,
    })
    setMcpServerIds((bot.mcp_servers || []).map((s) => s.id))

    const docSettingsMap: Record<string, any> = {}
    ;(bot.document_settings || []).forEach((setting: any) => {
      if (setting.document_id) {
        docSettingsMap[String(setting.document_id)] = {
          use_full_context: setting.use_full_context || false,
          rag_enabled: setting.rag_enabled,
          rag_top_k: setting.rag_top_k,
          rag_chunk_size: setting.rag_chunk_size,
          rag_chunk_overlap: setting.rag_chunk_overlap,
          rag_similarity_threshold: setting.rag_similarity_threshold,
          rag_max_context_length: setting.rag_max_context_length,
        }
      }
    })
    dispatch(
      setUploadedFiles(
        (bot.files || []).map((file: any) => ({
          ...file,
          rag_settings: docSettingsMap[String(file.id)],
        }))
      )
    )
  }, [bot, dispatch])

  useEffect(() => {
    return () => {
      dispatch(setIsAIPlayerTestChatOpen(false))
      dispatch(clearAllAIPlayerOpenChatBoxes())
      dispatch(clearAIPlayerTestChatMessages())
    }
  }, [dispatch])

  const documentSettingsData = useMemo(() => {
    const data: Record<string, any> = {}
    uploadedFiles.forEach((f) => {
      if (f.rag_settings) {
        data[String(f.id)] = f.rag_settings
      }
    })
    return data
  }, [uploadedFiles])

  const toggleMCPServer = (serverId: number) => {
    if (chatBoxesOpen) return
    setMcpServerIds((prev) =>
      prev.includes(serverId)
        ? prev.filter((id) => id !== serverId)
        : [...prev, serverId]
    )
  }

  const scrollTo = (section: AIPlayerSection) => {
    setActiveSection(section)
    const el = document.getElementById(section)
    if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' })
  }

  const handleSave = async () => {
    if (!name.trim() || !persona.trim()) {
      toast.error('Name and persona instructions are required')
      return
    }
    setIsSaving(true)
    try {
      await dispatch(
        updateDummyBotThunk({
          botId: bot.id,
          payload: {
            name: name.trim(),
            persona_instructions: persona.trim(),
            lock_during_active_round: lockDuringRound,
            chatbot_llm: chatbotLlm,
            temperature: llmConfig.temperature,
            max_tokens: llmConfig.maxTokens,
            history_limit: llmConfig.historyLimit,
            rag_enabled: ragConfig.ragEnabled,
            rag_top_k: ragConfig.ragTopK,
            rag_chunk_size: ragConfig.ragChunkSize,
            rag_chunk_overlap: ragConfig.ragChunkOverlap,
            rag_similarity_threshold: ragConfig.ragSimilarityThreshold,
            rag_max_context_length: ragConfig.ragMaxContextLength,
            file_ids: uploadedFiles.map((f) => f.id),
            document_settings_data:
              Object.keys(documentSettingsData).length > 0
                ? documentSettingsData
                : undefined,
            mcp_server_ids: mcpServerIds,
            mcp_enabled: mcpServerIds.length > 0,
          },
        })
      ).unwrap()
      toast.success('AI player configuration saved')
      await onSaved?.()
    } catch (error: any) {
      toast.error(error || 'Failed to save AI player configuration')
    } finally {
      setIsSaving(false)
    }
  }

  const handleTestChatClick = () => {
    dispatch(setIsAIPlayerTestChatOpen(true))
    dispatch(addAIPlayerTestChatBox())
  }

  const handlePreviousChatsClick = () => {
    dispatch(getPreviousAIPlayerChats())
    setPreviousChatModalOpen(true)
  }

  const handleChatClose = () => {
    dispatch(closeAIPlayerTestChat())
    dispatch(clearAIPlayerTestChatMessages())
    dispatch(setIsAIPlayerTestChatOpen(false))
    dispatch(removeAIPlayerTestChatBox())
  }

  const handleCloseChatBox = (chatId: number) => {
    dispatch(removeAIPlayerOpenChatBox(chatId))
  }

  const handleViewChat = (chatId: number) => {
    const existingPreviousBox = openChatBoxes.find((box) => !box.isTestChat)
    if (existingPreviousBox) {
      dispatch(removeAIPlayerOpenChatBox(existingPreviousBox.id))
    }
    const selectedChat = existingTestChats.find((chat) => chat.id === chatId)
    if (selectedChat) {
      dispatch(addAIPlayerOpenChatBox({ chatData: selectedChat, isReadOnly: true }))
    }
  }

  return (
    <SidebarProvider>
      <div className='flex relative w-full min-h-screen'>
        <AIPlayerConfigSideBar
          activeSection={activeSection}
          onSectionChange={scrollTo}
        />
        <div className='flex flex-col flex-1'>
          <div className='overflow-y-auto flex-1 p-8 space-y-8'>
            <div id={AIPlayerSection.PERSONA} className='space-y-3'>
              <div className='flex items-center'>
                <h2 className='text-2xl font-semibold'>Persona & Behavior</h2>
                <div className='ml-auto flex items-center gap-3'>
                  <Button
                    className='bg-dark-blue hover:bg-dark-blue-hovered'
                    onClick={handlePreviousChatsClick}
                    type='button'
                    disabled={chatBoxesOpen}
                  >
                    <span className='flex items-center gap-2'>
                      <History className='w-4 h-4' />
                      Previous Chats
                    </span>
                  </Button>
                  <Button
                    className='bg-dark-blue hover:bg-dark-blue-hovered'
                    onClick={handleTestChatClick}
                    type='button'
                    disabled={chatBoxesOpen}
                  >
                    <span className='flex items-center gap-2'>
                      <MessageSquare className='w-4 h-4' />
                      Test Chat
                    </span>
                  </Button>
                </div>
              </div>
              <div className='space-y-2'>
                <Label>AI Player Name</Label>
                <Input
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  disabled={chatBoxesOpen}
                />
              </div>
              <div className='space-y-2'>
                <Label>Persona Instructions</Label>
                <Textarea
                  value={persona}
                  rows={5}
                  onChange={(e) => setPersona(e.target.value)}
                  disabled={chatBoxesOpen}
                />
              </div>
              <div className='flex items-center gap-2'>
                <Switch
                  checked={lockDuringRound}
                  onCheckedChange={setLockDuringRound}
                  disabled={chatBoxesOpen}
                />
                <Label>Lock instructions during active negotiation phase</Label>
              </div>
            </div>

            <div id={AIPlayerSection.MODEL_CONFIG}>
              <LLMConfigSection
                config={llmConfig}
                onConfigChange={setLlmConfig}
                disabled={chatBoxesOpen}
              />
            </div>

            <div id={AIPlayerSection.RAG_CONFIG}>
              <RAGConfigSection
                config={ragConfig}
                onConfigChange={setRagConfig}
                disabled={chatBoxesOpen}
              />
            </div>

            <div id={AIPlayerSection.UPLOAD}>
              <FileUpload
                chatbotid={String(bot.id)}
                disabled={chatBoxesOpen}
                uploadTarget='dummy_bot'
                advisorRAGDefaults={ragConfig}
              />
            </div>

            <div id={AIPlayerSection.LLM_PREFERENCE} className='space-y-6'>
              <h2 className='text-2xl font-semibold text-foreground'>
                Preferred LLM Model
              </h2>
              <div className='space-y-4 mt-6 p-4 border border-blue-200 bg-blue-50 rounded-lg'>
                <div className='space-y-2'>
                  <label className='text-sm font-medium text-foreground'>
                    Select LLM Model
                  </label>
                  <Select
                    value={chatbotLlm}
                    onValueChange={(v) => setChatbotLlm(v as LLMChoice)}
                    disabled={chatBoxesOpen}
                  >
                    <SelectTrigger className='h-10 bg-white text-sm w-[300px]'>
                      <SelectValue placeholder='Select preferred LLM model' />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value={LLMChoice.CLAUDE}>Claude</SelectItem>
                      <SelectItem value={LLMChoice.OPENAI}>OpenAI</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </div>

            <div id={AIPlayerSection.MCP_SERVER} className='space-y-6'>
              <h2 className='text-2xl font-semibold text-foreground'>
                MCP Server Assignment
              </h2>
              <Card className={chatBoxesOpen ? 'opacity-50' : ''}>
                <CardContent className='py-6'>
                  {servers.filter((s) => s.enabled).length === 0 ? (
                    <div className='py-4 text-center'>
                      <p className='mb-2 text-sm text-muted-foreground'>
                        No MCP servers available.
                      </p>
                      <p className='text-xs text-muted-foreground'>
                        Configure MCP servers in room settings, or connect a
                        research account in your profile.
                      </p>
                    </div>
                  ) : (
                    <div className='space-y-4'>
                      <div>
                        <Label className='block mb-3 text-sm font-medium'>
                          Available MCP Servers
                        </Label>
                        <p className='mb-4 text-xs text-muted-foreground'>
                          Select which MCP servers this AI player can use.
                          Multiple servers can be selected.
                        </p>
                      </div>
                      <div className='space-y-3'>
                        {servers.filter((s) => s.enabled).map((server) => {
                          const isRemote = !('room' in server)
                          const ownerNotConnected =
                            isRemote && !ownerConnectedIds.has(server.id)
                          const isChecked = mcpServerIds.includes(server.id)
                          return (
                            <div
                              key={server.id}
                              className={cn(
                                'flex items-start p-3 space-x-3 rounded-lg border',
                                chatBoxesOpen || ownerNotConnected
                                  ? 'cursor-not-allowed opacity-60'
                                  : 'hover:bg-accent'
                              )}
                            >
                              <Checkbox
                                id={`ai-mcp-${server.id}`}
                                checked={isChecked}
                                onChange={() =>
                                  !ownerNotConnected &&
                                  toggleMCPServer(server.id)
                                }
                                disabled={chatBoxesOpen || ownerNotConnected}
                              />
                              <div className='flex-1 min-w-0'>
                                <div className='flex items-center gap-2 flex-wrap'>
                                  <Label
                                    htmlFor={`ai-mcp-${server.id}`}
                                    className={cn(
                                      'font-medium',
                                      !ownerNotConnected && 'cursor-pointer'
                                    )}
                                  >
                                    {server.name}
                                  </Label>
                                  {isRemote && (
                                    <Badge
                                      variant='outline'
                                      className='text-xs gap-1 py-0'
                                    >
                                      <FlaskConical className='h-2.5 w-2.5' />
                                      Research
                                    </Badge>
                                  )}
                                  {isRemote &&
                                    ownerConnectedIds.has(server.id) && (
                                      <Badge
                                        variant='outline'
                                        className='text-xs text-green-600 border-green-300 bg-green-50 py-0'
                                      >
                                        Connected
                                      </Badge>
                                    )}
                                </div>
                                <p className='mt-1 text-xs text-muted-foreground'>
                                  {server.description ||
                                    (!isRemote && 'command' in server
                                      ? `${server.command} ${server.args.join(' ')}`
                                      : '')}
                                </p>
                                {ownerNotConnected && (
                                  <p className='mt-1 flex items-center gap-1 text-xs text-amber-600'>
                                    <AlertCircle className='h-3 w-3' />
                                    Room owner hasn&apos;t connected this
                                    service yet
                                  </p>
                                )}
                              </div>
                            </div>
                          )
                        })}
                      </div>
                    </div>
                  )}
                </CardContent>
              </Card>
            </div>
          </div>
          <div className='flex justify-end p-6 border-t'>
            <Button
              onClick={handleSave}
              disabled={isSaving || chatBoxesOpen}
              className='bg-dark-blue hover:bg-dark-blue-hovered'
            >
              {isSaving ? 'Saving...' : 'Save AI Player Configuration'}
            </Button>
          </div>
        </div>
        <ChatBoxesRenderer
          openChatBoxes={openChatBoxes}
          onTestChatClose={handleChatClose}
          onCloseChatBox={handleCloseChatBox}
          testChatType={TestChatType.AIPlayer}
        />
        <PreviousAIPlayerTestChatsModal
          isOpen={previousChatModalOpen}
          onClose={() => setPreviousChatModalOpen(false)}
          onViewChat={handleViewChat}
        />
      </div>
    </SidebarProvider>
  )
}
